import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { conversions, storeUTMParams, trackPageView } from '../utils/analytics';

const MilitaryDeployment = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Store UTM parameters for attribution
    storeUTMParams();
    
    // Track page view
    trackPageView('/military-deployment', 'Military Deployment - Always Here');
    
    // Track landing page visit
    conversions.militaryLandingViewed();
  }, []);

  const handleGetStarted = () => {
    conversions.militaryCTAClicked();
    navigate('/register?plan=military');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 to-blue-700">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        <div className="text-center">
          <div className="inline-block bg-yellow-400 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            🎖️ HELPING MILITARY FAMILIES STAY CONNECTED
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Never Miss Bedtime Again
          </h1>
          
          <p className="text-2xl text-blue-100 mb-4 max-w-3xl mx-auto">
            Deploy with peace of mind. Your kids hear your voice every night, 
            even when you're 8,000 miles away.
          </p>
          
          <p className="text-xl text-blue-200 mb-8 max-w-2xl mx-auto">
            Record 365 bedtime stories in just 2 hours. Automated delivery every night. 
            Stay connected during deployment.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <button
              onClick={handleGetStarted}
              className="bg-yellow-400 text-blue-900 px-8 py-4 rounded-lg text-xl font-bold hover:bg-yellow-300 transition shadow-lg"
            >
              Start Recording Free →
            </button>
            <button
              onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg text-xl font-semibold hover:bg-white hover:text-blue-900 transition"
            >
              Watch How It Works
            </button>
          </div>
          
          <p className="text-blue-200 text-sm">
            ✅ No credit card required • ✅ Military discount available • ✅ Cancel anytime
          </p>
        </div>
        
        {/* Hero Image/Video Placeholder */}
        <div className="mt-12 rounded-xl overflow-hidden shadow-2xl">
          <div className="bg-gray-800 aspect-video flex items-center justify-center">
            <div className="text-center">
              <div className="text-6xl mb-4">🎥</div>
              <p className="text-white text-xl">Video: Service member recording bedtime stories</p>
              <p className="text-gray-400 mt-2">Kids' faces lighting up hearing parent's voice</p>
            </div>
          </div>
        </div>
      </div>

      {/* Key Benefits */}
      <div className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-blue-900">2 Hours</div>
              <div className="text-gray-600 mt-2">Setup Time</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-900">365 Days</div>
              <div className="text-gray-600 mt-2">Stay Connected</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-900">24/7</div>
              <div className="text-gray-600 mt-2">Access Anytime</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-blue-900">$39/mo</div>
              <div className="text-gray-600 mt-2">Military Discount</div>
            </div>
          </div>
        </div>
      </div>

      {/* Problem Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Deployment Is Hard Enough. Don't Miss Your Kids' Childhood Too.
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
              <h3 className="text-xl font-bold text-red-900 mb-3">❌ Without This Service:</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Miss 365 bedtimes during deployment</li>
                <li>• Kids struggle with separation anxiety</li>
                <li>• Spouse handles bedtime alone (exhausted)</li>
                <li>• Unreliable video calls (time zones, WiFi)</li>
                <li>• Kids forget what you sound like</li>
              </ul>
            </div>
            
            <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
              <h3 className="text-xl font-bold text-green-900 mb-3">✅ With Always Here:</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Your voice tucks them in every night</li>
                <li>• Kids feel connected and secure</li>
                <li>• Spouse gets bedtime help (30 min saved)</li>
                <li>• Works anywhere, anytime (no WiFi needed)</li>
                <li>• You're present for their entire childhood</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div id="how-it-works" className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Set Up in 3 Simple Steps
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">🎙️</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">1. Record Stories</h3>
              <p className="text-gray-600">
                Spend 2 hours recording bedtime stories, songs, and messages. 
                Our AI helps guide you through the process.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">📅</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">2. Set Schedule</h3>
              <p className="text-gray-600">
                Choose when messages are delivered. Daily bedtime, birthdays, 
                first day of school, or anytime your kids need you.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">💙</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">3. Deploy in Peace</h3>
              <p className="text-gray-600">
                Your kids receive your voice messages automatically. 
                You stay connected throughout your entire deployment.
              </p>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <button
              onClick={handleGetStarted}
              className="bg-blue-600 text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-blue-700 transition shadow-lg"
            >
              Get Started Free →
            </button>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            What Military Families Are Saying
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 text-xl">★★★★★</div>
              </div>
              <p className="text-gray-700 mb-4 italic">
                "Being able to record bedtime stories before deployment gave me peace of mind. 
                My kids could hear my voice every night, even when I couldn't call."
              </p>
              <p className="text-gray-900 font-semibold">— Military Parent (Beta Tester)</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 text-xl">★★★★★</div>
              </div>
              <p className="text-gray-700 mb-4 italic">
                "The AI interview made it so easy. In 2 hours I had enough content for 
                the entire deployment. My family loved hearing my voice every day."
              </p>
              <p className="text-gray-900 font-semibold">— Service Member (Beta Tester)</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 text-xl">★★★★★</div>
              </div>
              <p className="text-gray-700 mb-4 italic">
                "Having automated bedtime messages during deployment made single parenting 
                so much easier. The kids felt connected to their parent every night."
              </p>
              <p className="text-gray-900 font-semibold">— Military Spouse (Beta Tester)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Everything You Need to Stay Connected
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex items-start space-x-4">
              <div className="text-3xl">📱</div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">WhatsApp Delivery</h3>
                <p className="text-gray-600">Messages delivered via WhatsApp. Kids can listen anytime, anywhere.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="text-3xl">🎭</div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Voice Cloning</h3>
                <p className="text-gray-600">AI clones your voice for authentic, natural-sounding messages.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="text-3xl">📅</div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Automated Scheduling</h3>
                <p className="text-gray-600">Set it once, forget it. Messages delivered automatically every day.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="text-3xl">🎂</div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Special Occasions</h3>
                <p className="text-gray-600">Birthday messages, first day of school, holidays, and more.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="text-3xl">☁️</div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Secure Cloud Storage</h3>
                <p className="text-gray-600">Military-grade encryption. Your messages are safe and private.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="text-3xl">🌍</div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Works Anywhere</h3>
                <p className="text-gray-600">No WiFi needed. Works in any country, any time zone.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="bg-blue-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-4">
            Military Family Pricing
          </h2>
          <p className="text-xl text-blue-200 text-center mb-12">
            Special pricing for those who serve
          </p>
          
          <div className="bg-white rounded-xl p-8 shadow-2xl">
            <div className="text-center mb-6">
              <div className="inline-block bg-yellow-400 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                🎖️ MILITARY DISCOUNT
              </div>
              <div className="text-5xl font-bold text-gray-900 mb-2">
                $39<span className="text-2xl text-gray-600">/month</span>
              </div>
              <p className="text-gray-600">Regular price: $49/month</p>
            </div>
            
            <ul className="space-y-3 mb-8">
              <li className="flex items-center text-gray-700">
                <span className="text-green-500 mr-3">✓</span>
                Unlimited recordings and messages
              </li>
              <li className="flex items-center text-gray-700">
                <span className="text-green-500 mr-3">✓</span>
                Voice cloning included
              </li>
              <li className="flex items-center text-gray-700">
                <span className="text-green-500 mr-3">✓</span>
                Daily automated delivery
              </li>
              <li className="flex items-center text-gray-700">
                <span className="text-green-500 mr-3">✓</span>
                Up to 4 children
              </li>
              <li className="flex items-center text-gray-700">
                <span className="text-green-500 mr-3">✓</span>
                Special occasion messages
              </li>
              <li className="flex items-center text-gray-700">
                <span className="text-green-500 mr-3">✓</span>
                Cancel anytime during deployment
              </li>
            </ul>
            
            <button
              onClick={handleGetStarted}
              className="w-full bg-blue-600 text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-blue-700 transition"
            >
              Start Free Trial →
            </button>
            
            <p className="text-center text-gray-600 text-sm mt-4">
              First month free • No credit card required • Cancel anytime
            </p>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="border-b pb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                How long does it take to set up?
              </h3>
              <p className="text-gray-600">
                About 2 hours to record enough content for a full deployment. You can record 
                100+ stories in one sitting, or add more anytime before you deploy.
              </p>
            </div>
            
            <div className="border-b pb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                What if I don't have time to record before deployment?
              </h3>
              <p className="text-gray-600">
                Our AI interviewer can help you create 50+ messages in just 30 minutes. 
                We'll guide you through the most important stories and messages.
              </p>
            </div>
            
            <div className="border-b pb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Can I add more messages while deployed?
              </h3>
              <p className="text-gray-600">
                Yes! If you have internet access, you can record and add new messages anytime 
                from anywhere in the world.
              </p>
            </div>
            
            <div className="border-b pb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Is my data secure?
              </h3>
              <p className="text-gray-600">
                Absolutely. We use military-grade encryption and secure cloud storage. 
                Your messages are private and protected.
              </p>
            </div>
            
            <div className="border-b pb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                What happens after deployment ends?
              </h3>
              <p className="text-gray-600">
                You can pause or cancel anytime. Many families keep using it for business 
                trips, training, or just as a special bedtime routine.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Don't Deploy Without This
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Record once, stay connected throughout your entire deployment
          </p>
          <button
            onClick={handleGetStarted}
            className="bg-yellow-400 text-blue-900 px-12 py-5 rounded-lg text-2xl font-bold hover:bg-yellow-300 transition shadow-2xl"
          >
            Start Recording Free →
          </button>
          <p className="text-blue-200 mt-4">
            Set up in 2 hours • Deploy in peace • Stay connected for 365 nights
          </p>
        </div>
      </div>
    </div>
  );
};

export default MilitaryDeployment;
